// libraries
import clsx from 'clsx'
import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from 'react'

// components
import Image from '/react4xp/components/Image'
import AudioToggle from '/react4xp/components/AudioToggle'

// svg
import { Picture, Play, Paper } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
export interface CardProps {
	className?: string
	variation?: 'media' | 'news'
	color?: 'dark-green' | 'light-green'
	icon?: 'image' | 'video' | 'article'
	image?: {
		src?: string
		alt?: string
	}
	video?: string
	youtubeVideoId?: string
	youtubeVideoAutoplay?: boolean
	title?: string
	intro?: string
	link?: {
		href?: string
		external?: boolean
	}
	onMouseEnter?: () => void
	lazyLoadVideo?: boolean
}

export interface CardRef {
	playVideo: () => void
	pauseVideo: () => void
	hasVideo: () => boolean
}

const Card = forwardRef<CardRef, CardProps>(({
	className,
	variation,
	color,
	icon,
	image,
	video,
	youtubeVideoId,
	youtubeVideoAutoplay,
	title,
	intro,
	link,
	onMouseEnter,
	lazyLoadVideo = false
}, ref) => {
	const youtubePlayerRef = useRef<any>(null)
	const youtubeIframeRef = useRef<HTMLDivElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const divRef = useRef<HTMLDivElement>(null)

	const [isMuted, setIsMuted] = useState(true)
	const [isPaused, setIsPaused] = useState(true)
	const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false)

	/* ---------------------------------------------------------------------
	 * Exposed imperative API
	 * ------------------------------------------------------------------- */
	useImperativeHandle(ref, () => ({
		playVideo: () => {
			if (video && videoRef.current) {
				videoRef.current.play().catch(() => {})
				setIsPaused(false)
				return
			}

			if (youtubeVideoId && youtubePlayerRef.current?.playVideo) {
				youtubePlayerRef.current.playVideo()
				setIsPaused(false)
			}
		},
		pauseVideo: () => {
			if (video && videoRef.current) {
				videoRef.current.pause()
				setIsPaused(true)
				return
			}

			if (youtubeVideoId && youtubePlayerRef.current?.pauseVideo) {
				youtubePlayerRef.current.pauseVideo()
				setIsPaused(true)
			}
		},
		hasVideo: () => !!(video || youtubeVideoId)
	}), [video, youtubeVideoId])

	/* ---------------------------------------------------------------------
	 * IntersectionObserver – decide when to load YouTube player
	 * ------------------------------------------------------------------- */
	useEffect(() => {
		if (!youtubeVideoId || video || !youtubeIframeRef.current) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setShouldLoadPlayer(true)
					observer.disconnect()
				}
			},
			{
				root: null,
				rootMargin: '200px',
				threshold: 0.25
			}
		)

		observer.observe(youtubeIframeRef.current)

		return () => observer.disconnect()
	}, [youtubeVideoId, video])

	/* ---------------------------------------------------------------------
	 * YouTube player creation (lazy)
	 * ------------------------------------------------------------------- */
	useEffect(() => {
		if (!youtubeVideoId || video || !shouldLoadPlayer) return

		let isMounted = true

		const createPlayer = () => {
			if (!isMounted || !youtubeIframeRef.current) return

			try {
				youtubePlayerRef.current = new (window as any).YT.Player(
					youtubeIframeRef.current,
					{
						videoId: youtubeVideoId,
						host: 'https://www.youtube-nocookie.com',
						playerVars: {
							origin: window.location.origin,
							autoplay: 0,
							controls: 0,
							modestbranding: 1,
							rel: 0,
							fs: 0,
							iv_load_policy: 3,
							disablekb: 1,
							loop: 1,
							playlist: youtubeVideoId
						},
						events: {
							onReady: (event: any) => {
								event.target.mute()
								setIsMuted(true)
								setIsPaused(true)

								if (youtubeVideoAutoplay) {
									event.target.playVideo()
									setIsPaused(false)
								}
							},
							onStateChange: (event: any) => {
								if (event.data === 1) setIsPaused(false)
								if (event.data === 2 || event.data === 0) setIsPaused(true)
							}
						}
					}
				)
			} catch (error) {
				youtubePlayerRef.current = null
				console.error(`Could not create YouTube player for video id "${youtubeVideoId}"`, error)
			}
		}

		// Load API once
		if (!(window as any).YT) {
			if (!document.getElementById('youtube-iframe-api')) {
				const tag = document.createElement('script')
				tag.id = 'youtube-iframe-api'
				tag.src = 'https://www.youtube.com/iframe_api'
				document.body.appendChild(tag)
			}

			const prev = (window as any).onYouTubeIframeAPIReady
			;(window as any).onYouTubeIframeAPIReady = () => {
				if (prev) prev()
				createPlayer()
			}
		} else {
			createPlayer()
		}

		return () => {
			isMounted = false
			youtubePlayerRef.current?.destroy()
		}
	}, [youtubeVideoId, shouldLoadPlayer, video, youtubeVideoAutoplay])

	/* ---------------------------------------------------------------------
	 * Native video play/pause tracking
	 * ------------------------------------------------------------------- */
	useEffect(() => {
		if (!video || !videoRef.current) return

		const el = videoRef.current
		const onPlay = () => setIsPaused(false)
		const onPause = () => setIsPaused(true)

		el.addEventListener('play', onPlay)
		el.addEventListener('pause', onPause)

		return () => {
			el.removeEventListener('play', onPlay)
			el.removeEventListener('pause', onPause)
		}
	}, [video])

	const hasVideo = !!(video || youtubeVideoId)

	const toggleMute = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		if (video && videoRef.current) {
			videoRef.current.muted = !videoRef.current.muted
			setIsMuted(videoRef.current.muted)
		} else if (youtubeVideoId && youtubePlayerRef.current) {
			if (isMuted) {
				youtubePlayerRef.current.unMute()
				setIsMuted(false)
			} else {
				youtubePlayerRef.current.mute()
				setIsMuted(true)
			}
		}
	}

	/* ---------------------------------------------------------------------
	 * Render
	 * ------------------------------------------------------------------- */
	return (
		<div
			ref={divRef}
			className={clsx(
				'card-component',
				variation === 'news' && 'card-component--news',
				hasVideo && 'card-component--video',
				className
			)}
			onMouseEnter={onMouseEnter}
		>
			{link?.href && (
				<a
					href={link.href}
					target={link.external ? '_blank' : '_self'}
					className='card-component__link'
				>
					{title && <p>{title}</p>}
				</a>
			)}

			{hasVideo && (
				<AudioToggle
					isMuted={isMuted}
					isPaused={isPaused}
					onClick={toggleMute}
				/>
			)}

			<div className='card-component__media-wrapper cover'>
				{!video && image?.src && (
					<Image
						src={{ src: image.src, width: 500, height: 750 }}
						alt={image.alt || ''}
						className='cover'
						sizes='(max-width: 576px) 90vw, 500px'
					/>
				)}

				{video && (
					<video
						ref={videoRef}
						src={`${video}#t=0.001`}
						muted={isMuted}
						loop
						playsInline
						preload={lazyLoadVideo ? 'none' : 'auto'}
						className='cover'
					/>
				)}

				{!video && youtubeVideoId && (
					<div
						ref={youtubeIframeRef}
						style={{ width: '100%', height: '100%' }}
					/>
				)}
			</div>

			{variation === 'media' && (
				<div className='card-component__bottom card-component__bottom--media'>
					<div className={clsx(
						'card-component__bottom__icon',
						color === 'dark-green' && 'bg-green',
						color === 'light-green' && 'bg-green-light'
					)}>
						{icon === 'image' && <Picture />}
						{icon === 'video' && <Play />}
						{icon === 'article' && <Paper />}
					</div>
					<p className='body white'>{title}</p>
				</div>
			)}

			{variation === 'news' && (
				<div className='card-component__bottom card-component__bottom--news'>
					<h3 className='lead-paragraph white'>{title}</h3>
					<p className='body white'>{intro}</p>
				</div>
			)}
		</div>
	)
})

Card.displayName = 'Card'
export default Card